import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { Task, TaskUpdate } from './types/todoTypes';

export function useTodoTasks() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const addTask = useCallback(async (listId: string, text: string, deadline?: Date | null) => {
    if (!text.trim()) return null;
    setLoading(true);
    setError(null);

    try {
      console.log('Starting to add task:', { listId, text, deadline });
      
      // Get current user if available
      let userId = 'anonymous';
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) userId = user.id;
      } catch (authError) {
        console.log('No authenticated user, using anonymous');
      }
      
      const taskData = { 
        list_id: listId, 
        text: text.trim(),
        completed: false,
        created_by: userId,
        deadline: deadline?.toISOString() || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('Inserting task data:', taskData);
      
      const { data, error: supabaseError } = await supabase
        .from('todo_tasks')
        .insert([taskData])
        .select()
        .single();

      if (supabaseError) {
        console.error('Supabase error adding task:', supabaseError);
        if (supabaseError.code === '42501') {
          // This error typically means the list was deleted or user doesn't have permission
          const error = new Error('The list was deleted or you no longer have permission');
          error.name = 'ListDeletedError';
          throw error;
        }
        throw supabaseError;
      }
      
      console.log('Successfully added task:', data);
      return data;
    } catch (err) {
      console.error('Error in addTask:', err);
      setError(err instanceof Error ? err : new Error('Failed to add task'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTask = useCallback(async (taskId: string, updates: TaskUpdate) => {
    if (!taskId) {
      console.error('No task ID provided for update');
      return null;
    }
    
    setLoading(true);
    setError(null);

    try {
      console.log('Updating task:', { taskId, updates });
      
      const updateData = {
        ...updates,
        updated_at: new Date().toISOString()
      };
      
      console.log('Sending update to database:', updateData);
      
      const { data, error: supabaseError } = await supabase
        .from('todo_tasks')
        .update(updateData)
        .eq('id', taskId)
        .select()
        .single();

      if (supabaseError) {
        console.error('Supabase error updating task:', supabaseError);
        throw supabaseError;
      }
      
      console.log('Successfully updated task:', data);
      return data;
    } catch (err) {
      console.error('Error in updateTask:', err);
      setError(err instanceof Error ? err : new Error('Failed to update task'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteTask = useCallback(async (taskId: string) => {
    setLoading(true);
    setError(null);

    try {
      const { error: supabaseError } = await supabase
        .from('todo_tasks')
        .delete()
        .eq('id', taskId);

      if (supabaseError) throw supabaseError;
      return true;
    } catch (err) {
      console.error('Error deleting task:', err);
      setError(err instanceof Error ? err : new Error('Failed to delete task'));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    addTask,
    updateTask,
    deleteTask,
  };
}
